import { Monitor, Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import {
  formatThemePreferenceLabel,
  useThemePreferenceControls,
} from '@/hooks/useThemePreferenceControls'

const themeIcons = {
  light: Sun,
  dark: Moon,
  system: Monitor,
} as const

interface ThemeCycleToggleProps {
  className?: string
  iconClassName?: string
  showLabel?: boolean
  labelClassName?: string
  variant?: 'icon' | 'pill'
}

export default function ThemeCycleToggle({
  className,
  iconClassName,
  showLabel = false,
  labelClassName,
  variant = 'icon',
}: ThemeCycleToggleProps) {
  const { themePreference, resolvedTheme, nextThemePreference, cycleThemePreference } = useThemePreferenceControls()

  const CurrentIcon = themeIcons[themePreference]
  const currentLabel = formatThemePreferenceLabel(themePreference, resolvedTheme)
  const nextLabel = nextThemePreference === 'system'
    ? 'System'
    : formatThemePreferenceLabel(nextThemePreference, nextThemePreference)

  const handleClick = () => {
    cycleThemePreference().catch(() => {})
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'transition-colors',
        variant === 'icon'
          ? 'rounded-lg p-2.5 text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
          : 'inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-800',
        className
      )}
      aria-label={`Theme is ${currentLabel}. Switch to ${nextLabel}.`}
      title={`Theme: ${currentLabel}. Click to switch to ${nextLabel}.`}
    >
      <CurrentIcon className={cn('h-5 w-5 shrink-0', iconClassName)} />
      {showLabel && (
        <span className={cn('whitespace-nowrap', labelClassName)}>
          {currentLabel}
        </span>
      )}
    </button>
  )
}
